import React from "react";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaTimesCircle, FaBan, FaExclamationCircle } from "react-icons/fa";

// Mirrors backend's validate_hostname (AddCustomDomainAPIView / CustomDomainSerializer).
export const RESERVED_DOMAIN_SUFFIX =
  process.env.REACT_APP_RESERVED_DOMAIN_SUFFIX || "sajilolife.com";
// RFC 1123 hostname, mirrored from backend's HOSTNAME_RE: labels of
// 1-63 chars (alphanumeric + hyphens, no leading/trailing hyphen on the first
// label), joined by dots, ending in a letters-only TLD of 2+ chars. Backend
// already lowercases before matching, and getDomainRuleStatus below does the
// same, so no /i flag is needed here.
export const HOSTNAME_FORMAT_REGEX =
  /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.[a-z0-9-]{1,63})*\.[a-z]{2,}$/;

export function isReservedDomain(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return (
    normalized === RESERVED_DOMAIN_SUFFIX ||
    normalized.endsWith(`.${RESERVED_DOMAIN_SUFFIX}`)
  );
}

export enum DomainRuleStatus {
  IDLE = "idle",
  REQUIRED = "required",
  PROTOCOL = "protocol",
  SLASH_OR_COLON = "slash_or_colon",
  RESERVED = "reserved",
  WILDCARD = "wildcard",
  INVALID_FORMAT = "invalid_format",
  VALID = "valid",
  // Anything the backend rejected on submit that isn't re-derivable client-side
  // (duplicate domain, per-store domain limit reached, pending-by-another-vendor
  // grace period, etc). Surfaced here via a serverError prop after CreateCustomDomain fails.
  SERVER_ERROR = "server_error",
}

// Client-side mirror of backend's validate_hostname, run in the same order so the
// message a user sees while typing matches what the server would say on submit.
export function getDomainRuleStatus(
  rawValue: string,
  { touched }: { touched: boolean },
): DomainRuleStatus {
  const value = rawValue.trim().toLowerCase();
  if (!value) return touched ? DomainRuleStatus.REQUIRED : DomainRuleStatus.IDLE;
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return DomainRuleStatus.PROTOCOL;
  }
  if (value.includes("/") || value.includes(":")) {
    return DomainRuleStatus.SLASH_OR_COLON;
  }
  if (isReservedDomain(value)) return DomainRuleStatus.RESERVED;
  if (value.includes("*")) return DomainRuleStatus.WILDCARD;
  if (!HOSTNAME_FORMAT_REGEX.test(value)) return DomainRuleStatus.INVALID_FORMAT;
  return DomainRuleStatus.VALID;
}

export interface DomainServerError {
  message: string;
}

interface DomainValidationRuleEngineProps {
  value: string;
  touched: boolean;
  serverError?: DomainServerError | null;
}

const DomainValidationRuleEngine: React.FC<DomainValidationRuleEngineProps> = ({
  value,
  touched,
  serverError,
}) => {
  const { t } = useTranslation();
  const status = serverError
    ? DomainRuleStatus.SERVER_ERROR
    : getDomainRuleStatus(value, { touched });

  const RULE_CONFIG: Record<
    Exclude<DomainRuleStatus, DomainRuleStatus.IDLE>,
    {
      icon: React.ReactNode;
      badgeLabel: string;
      badgeClass: string;
      message: string;
      chip: string;
      containerClass: string;
    }
  > = {
    [DomainRuleStatus.VALID]: {
      icon: <FaCheckCircle className="text-success" />,
      badgeLabel: t("customDomains.ruleLooksGoodTag"),
      badgeClass: "bg-success-subtle text-success-emphasis",
      message: t("customDomains.ruleLooksGoodMessage"),
      chip: t("customDomains.ruleClientCheckChip"),
      containerClass: "border-success-subtle bg-success-subtle bg-opacity-25",
    },
    [DomainRuleStatus.REQUIRED]: {
      icon: <FaTimesCircle className="text-danger" />,
      badgeLabel: t("customDomains.ruleRequiredTag"),
      badgeClass: "bg-danger-subtle text-danger-emphasis",
      message: t("customDomains.ruleRequiredMessage"),
      chip: t("customDomains.ruleClientCheckChip"),
      containerClass: "border-danger-subtle bg-danger-subtle bg-opacity-25",
    },
    [DomainRuleStatus.PROTOCOL]: {
      icon: <FaTimesCircle className="text-danger" />,
      badgeLabel: t("customDomains.ruleProtocolTag"),
      badgeClass: "bg-danger-subtle text-danger-emphasis",
      message: t("customDomains.ruleProtocolMessage"),
      chip: t("customDomains.ruleClientCheckChip"),
      containerClass: "border-danger-subtle bg-danger-subtle bg-opacity-25",
    },
    [DomainRuleStatus.SLASH_OR_COLON]: {
      icon: <FaTimesCircle className="text-danger" />,
      badgeLabel: t("customDomains.ruleSlashColonTag"),
      badgeClass: "bg-danger-subtle text-danger-emphasis",
      message: t("customDomains.ruleSlashColonMessage"),
      chip: t("customDomains.ruleClientCheckChip"),
      containerClass: "border-danger-subtle bg-danger-subtle bg-opacity-25",
    },
    [DomainRuleStatus.RESERVED]: {
      icon: <FaBan className="text-danger" />,
      badgeLabel: t("customDomains.rulePlatformDomainTag"),
      badgeClass: "bg-warning-subtle text-warning-emphasis",
      message: t("customDomains.rulePlatformDomainMessage"),
      chip: t("customDomains.ruleClientCheckChip"),
      containerClass: "border-danger-subtle bg-danger-subtle bg-opacity-25",
    },
    [DomainRuleStatus.WILDCARD]: {
      icon: <FaTimesCircle className="text-danger" />,
      badgeLabel: t("customDomains.ruleWildcardTag"),
      badgeClass: "bg-danger-subtle text-danger-emphasis",
      message: t("customDomains.ruleWildcardMessage"),
      chip: t("customDomains.ruleClientCheckChip"),
      containerClass: "border-danger-subtle bg-danger-subtle bg-opacity-25",
    },
    [DomainRuleStatus.INVALID_FORMAT]: {
      icon: <FaTimesCircle className="text-danger" />,
      badgeLabel: t("customDomains.ruleFormatTag"),
      badgeClass: "bg-danger-subtle text-danger-emphasis",
      message: t("customDomains.ruleFormatMessage"),
      chip: t("customDomains.ruleClientCheckChip"),
      containerClass: "border-danger-subtle bg-danger-subtle bg-opacity-25",
    },
    [DomainRuleStatus.SERVER_ERROR]: {
      icon: <FaExclamationCircle className="text-danger" />,
      badgeLabel: t("customDomains.ruleServerErrorTag"),
      badgeClass: "bg-warning-subtle text-warning-emphasis",
      message: serverError?.message || t("customDomains.ruleServerErrorFallbackMessage"),
      chip: t("customDomains.ruleServerResponseChip"),
      containerClass: "border-danger-subtle bg-danger-subtle bg-opacity-25",
    },
  };

  return (
    <div className="border rounded-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 p-3 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <span className="text-uppercase small fw-semibold text-muted">
            {t("customDomains.validationRuleEngine")}
          </span>
        </div>
        <span className="small text-muted">{t("customDomains.interactivePreview")}</span>
      </div>

      <div className="p-3">
        {status === DomainRuleStatus.IDLE ? (
          <div className="small text-muted fst-italic">
            {t("customDomains.ruleAwaitingInput")}
          </div>
        ) : (
          (() => {
            const config = RULE_CONFIG[status];
            return (
              <div
                className={`d-flex align-items-start justify-content-between gap-3 border rounded-3 p-3 ${config.containerClass}`}
              >
                <div className="d-flex align-items-start gap-2">
                  <span className="mt-1">{config.icon}</span>
                  <div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="fw-semibold text-break">{value.trim()}</span>
                      <span className={`badge fw-normal ${config.badgeClass}`}>
                        {config.badgeLabel}
                      </span>
                    </div>
                    <div className="small">{config.message}</div>
                  </div>
                </div>
                <span className="badge fw-normal text-nowrap bg-light text-dark border">
                  {config.chip}
                </span>
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default DomainValidationRuleEngine;
